import React, { useMemo, useState } from 'react';
import { Check, Crown, Minus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  formatWsjPedidosPrice,
  getWsjPedidosAnnualBundle,
  getWsjPedidosPlanMonthlyPrices,
  WSJ_PEDIDOS_ANNUAL_MONTHS_PAID
} from '@/lib/wsjPedidosMoney';

/**
 * Planos WSJ Pedidos — conteúdo comercial da landing (preços e limites alinhados à operação real).
 */
export function WsjPedidosPlansSection({ language, copy, demoUrl, adminUrl, onWhatsapp }) {
  const [cycle, setCycle] = useState('monthly');
  const prices = useMemo(() => getWsjPedidosPlanMonthlyPrices(language), [language]);

  const pro = useMemo(
    () => ({
      monthly: prices.pro,
      ...getWsjPedidosAnnualBundle(prices.pro)
    }),
    [prices.pro]
  );

  const premium = useMemo(
    () => ({
      monthly: prices.premium,
      ...getWsjPedidosAnnualBundle(prices.premium)
    }),
    [prices.premium]
  );

  const fmt = (n) => formatWsjPedidosPrice(n, language);

  const proDisplay = cycle === 'monthly' ? pro.monthly : pro.effectiveMonthly;
  const premiumDisplay = cycle === 'monthly' ? premium.monthly : premium.effectiveMonthly;

  return (
    <section id="wsj-pedidos-plans" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
      <div className="text-center mb-8 sm:mb-10">
        <div className="inline-flex max-w-full min-w-0 flex-wrap items-center justify-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-200/90 mb-4">
          <Sparkles className="w-3.5 h-3.5 shrink-0 text-amber-400" aria-hidden />
          <span className="break-words text-center">{copy.plansBadge}</span>
        </div>
        <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words px-1">{copy.plansTitle}</h2>
        <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light leading-relaxed break-words">{copy.plansLead}</p>
        <p className="text-xs text-white/38 mt-4 max-w-2xl mx-auto leading-relaxed break-words">{copy.plansSustainabilityLead}</p>
      </div>

      <div className="flex flex-col items-center gap-2 mb-8 sm:mb-10">
        <div
          className="inline-flex rounded-full border border-white/[0.1] bg-slate-950/60 p-1 shadow-inner shadow-black/40"
          role="group"
          aria-label={copy.plansBillingGroupLabel}
        >
          <button
            type="button"
            onClick={() => setCycle('monthly')}
            className={`relative rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
              cycle === 'monthly' ? 'text-slate-950' : 'text-white/55 hover:text-white/80'
            }`}
          >
            {cycle === 'monthly' && (
              <span
                className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 shadow-md shadow-amber-900/25"
                aria-hidden
              />
            )}
            <span className="relative z-10">{copy.plansMonthly}</span>
          </button>
          <button
            type="button"
            onClick={() => setCycle('annual')}
            className={`relative rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
              cycle === 'annual' ? 'text-slate-950' : 'text-white/55 hover:text-white/80'
            }`}
          >
            {cycle === 'annual' && (
              <span
                className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 shadow-md shadow-amber-900/25"
                aria-hidden
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              {copy.plansYearly}
              <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wide text-emerald-700/90 bg-emerald-200/90 rounded-full px-2 py-0.5">
                {copy.plansSaveShort}
              </span>
            </span>
          </button>
        </div>
        <p className="text-[11px] text-white/40 text-center max-w-md leading-snug">{copy.plansAnnualAppliesTo}</p>
        {cycle === 'annual' && (
          <p className="text-xs text-emerald-300/85 font-medium text-center max-w-lg">
            {copy.plansAnnualExplain.replace('{n}', String(WSJ_PEDIDOS_ANNUAL_MONTHS_PAID))}
          </p>
        )}
      </div>

      <div className="grid gap-5 lg:gap-6 xl:grid-cols-3 items-stretch">
        {/* Starter */}
        <article className="group relative flex flex-col rounded-[1.75rem] border border-dashed border-white/15 bg-slate-950/30 p-6 sm:p-7 transition-all duration-300 hover:border-white/25 hover:bg-slate-950/45">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-lg font-bold text-white tracking-tight">{copy.planStarterName}</h3>
            <span className="shrink-0 rounded-full border border-white/15 bg-white/[0.05] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white/60">
              {copy.planStarterBadge}
            </span>
          </div>
          <p className="text-sm text-white/45 font-light">{copy.planStarterTagline}</p>
          <div className="mt-5 mb-2">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-3xl font-black text-white tabular-nums tracking-tight">{fmt(prices.starter)}</span>
              <span className="text-sm text-white/45 font-medium">{copy.planStarterPeriodLabel}</span>
            </div>
            <p className="text-xs text-amber-200/75 font-medium mt-3 leading-snug border border-amber-500/20 rounded-lg px-3 py-2 bg-amber-500/[0.06]">
              {copy.planStarterDisclaimer}
            </p>
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/35 mb-2">{copy.planStarterIncludedHeading}</p>
          <ul className="space-y-2.5 mb-5 flex-1">
            {copy.planStarterIncluded.map((line) => (
              <li key={line} className="flex gap-2.5 text-sm text-white/70 min-w-0">
                <Check className="w-4 h-4 shrink-0 text-emerald-400/85 mt-0.5" aria-hidden />
                <span className="leading-relaxed font-light break-words min-w-0">{line}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/35 mb-2">{copy.planStarterLimitsHeading}</p>
          <ul className="space-y-2 mb-6">
            {copy.planStarterLimits.map((line) => (
              <li key={line} className="flex gap-2 text-xs text-white/45 leading-relaxed min-w-0">
                <Minus className="w-3.5 h-3.5 shrink-0 text-white/30 mt-0.5" aria-hidden />
                <span className="break-words min-w-0">{line}</span>
              </li>
            ))}
          </ul>
          <div className="mt-auto flex flex-col gap-2">
            <Button asChild className="rounded-xl w-full bg-white/[0.08] border border-white/15 text-white hover:bg-white/12 font-semibold">
              <a href={demoUrl} target="_blank" rel="noopener noreferrer">
                {copy.planStarterCtaDemo}
              </a>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-xl w-full border-emerald-500/35 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15"
              onClick={onWhatsapp}
            >
              {copy.planStarterCtaWhatsapp}
            </Button>
          </div>
        </article>

        {/* Pro */}
        <article className="group relative flex flex-col rounded-[1.75rem] border border-white/[0.09] bg-gradient-to-b from-white/[0.06] to-slate-950/40 p-6 sm:p-8 shadow-[0_24px_80px_-40px_rgba(0,0,0,0.85)] transition-all duration-300 hover:border-amber-500/25 hover:shadow-[0_32px_90px_-36px_rgba(212,165,116,0.18)]">
          <div
            className="pointer-events-none absolute -inset-px rounded-[1.75rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-amber-500/12 via-transparent to-emerald-500/10 blur-2xl"
            aria-hidden
          />
          <div className="relative flex flex-col flex-1">
            <h3 className="text-lg font-bold text-white tracking-tight">{copy.planProName}</h3>
            <p className="text-sm text-white/45 mt-1 font-light">{copy.planProTagline}</p>
            {copy.planProDescription ? (
              <p className="text-sm text-white/45 mt-2 font-light leading-relaxed break-words">{copy.planProDescription}</p>
            ) : null}
            <div className="mt-6 mb-6">
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-3xl sm:text-4xl font-black text-white tabular-nums tracking-tight">{fmt(proDisplay)}</span>
                <span className="text-sm text-white/40 font-medium">{copy.plansPerMonth}</span>
              </div>
              {cycle === 'annual' && (
                <p className="text-xs text-white/40 mt-2">
                  {copy.plansAnnualBilled.replace('{amount}', fmt(pro.yearlyCharge))}
                </p>
              )}
              {cycle === 'annual' && pro.savingsPercent > 0 && (
                <p className="text-xs text-emerald-400/90 font-semibold mt-1.5">
                  {copy.plansSavingsLine
                    .replace('{pct}', String(pro.savingsPercent))
                    .replace('{amount}', fmt(pro.savedVsList))}
                </p>
              )}
            </div>
            {copy.planProFeaturesHeading ? (
              <p className="text-[11px] font-semibold uppercase tracking-wide text-white/35 mb-2">{copy.planProFeaturesHeading}</p>
            ) : null}
            <ul className="space-y-3 mb-5 flex-1">
              {copy.planProFeatures.map((line) => (
                <li key={line} className="flex gap-2.5 text-sm text-white/70 min-w-0">
                  <Check className="w-4 h-4 shrink-0 text-amber-400/90 mt-0.5" aria-hidden />
                  <span className="leading-relaxed font-light break-words min-w-0">{line}</span>
                </li>
              ))}
            </ul>
            {copy.planProImportantHeading && copy.planProImportantItems?.length ? (
              <div className="mb-5 rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-white/45 mb-2">{copy.planProImportantHeading}</p>
                <ul className="space-y-2">
                  {copy.planProImportantItems.map((line) => (
                    <li key={line} className="flex gap-2 text-xs text-white/50 leading-relaxed min-w-0">
                      <Minus className="w-3.5 h-3.5 shrink-0 text-white/30 mt-0.5" aria-hidden />
                      <span className="break-words min-w-0">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {copy.planProIdealHeading && copy.planProIdeal?.length ? (
              <div className="mb-5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-white/35 mb-2">{copy.planProIdealHeading}</p>
                <ul className="space-y-2">
                  {copy.planProIdeal.map((line) => (
                    <li key={line} className="flex gap-2 text-xs text-white/45 leading-relaxed min-w-0">
                      <Check className="w-3.5 h-3.5 shrink-0 text-amber-400/70 mt-0.5" aria-hidden />
                      <span className="break-words min-w-0">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {copy.planProFootnote ? (
              <p className="text-[11px] text-white/38 mb-4 leading-relaxed">{copy.planProFootnote}</p>
            ) : null}
            <div className="flex flex-col sm:flex-row gap-2.5 mt-auto">
              <Button asChild className="rounded-xl flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-md shadow-amber-900/20">
                <a href={demoUrl} target="_blank" rel="noopener noreferrer">
                  {copy.planCtaDemo}
                </a>
              </Button>
              <Button asChild variant="outline" className="rounded-xl flex-1 border-white/15 bg-white/[0.03] text-white hover:bg-white/10">
                <a href={adminUrl} target="_blank" rel="noopener noreferrer">
                  {copy.planCtaAdmin}
                </a>
              </Button>
            </div>
          </div>
        </article>

        {/* Premium */}
        <article className="group relative flex flex-col rounded-[1.75rem] border border-amber-400/35 bg-gradient-to-b from-amber-500/[0.12] via-slate-950/80 to-slate-950/95 p-6 sm:p-8 shadow-[0_28px_100px_-40px_rgba(212,165,116,0.35)] transition-all duration-300 hover:border-amber-400/50 hover:shadow-[0_36px_110px_-38px_rgba(212,165,116,0.42)] ring-1 ring-amber-500/15">
          <div
            className="pointer-events-none absolute inset-0 rounded-[1.75rem] bg-gradient-to-br from-amber-500/10 via-transparent to-emerald-500/8 opacity-90"
            aria-hidden
          />
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 sm:left-auto sm:right-8 sm:translate-x-0 z-10">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-slate-950/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-200 shadow-lg">
              <Crown className="w-3.5 h-3.5 text-amber-400" aria-hidden />
              {copy.planPremiumBadge}
            </span>
          </div>
          <div className="relative flex flex-col flex-1 pt-4">
            <h3 className="text-lg font-bold text-white tracking-tight">{copy.planPremiumName}</h3>
            <p className="text-sm text-amber-100/55 mt-1 font-light">{copy.planPremiumTagline}</p>
            {copy.planPremiumDescription ? (
              <p className="text-sm text-amber-100/45 mt-2 font-light leading-relaxed break-words">{copy.planPremiumDescription}</p>
            ) : null}
            <div className="mt-6 mb-6">
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-3xl sm:text-4xl font-black text-white tabular-nums tracking-tight">{fmt(premiumDisplay)}</span>
                <span className="text-sm text-white/40 font-medium">{copy.plansPerMonth}</span>
              </div>
              {cycle === 'annual' && (
                <p className="text-xs text-white/40 mt-2">
                  {copy.plansAnnualBilled.replace('{amount}', fmt(premium.yearlyCharge))}
                </p>
              )}
              {cycle === 'annual' && premium.savingsPercent > 0 && (
                <p className="text-xs text-emerald-300 font-semibold mt-1.5">
                  {copy.plansSavingsLine
                    .replace('{pct}', String(premium.savingsPercent))
                    .replace('{amount}', fmt(premium.savedVsList))}
                </p>
              )}
            </div>
            {copy.planPremiumFeaturesHeading ? (
              <p className="text-[11px] font-semibold uppercase tracking-wide text-white/35 mb-2">{copy.planPremiumFeaturesHeading}</p>
            ) : null}
            <ul className="space-y-3 mb-5 flex-1">
              {copy.planPremiumFeatures.map((line) => (
                <li key={line} className="flex gap-2.5 text-sm text-white/80 min-w-0">
                  <Check className="w-4 h-4 shrink-0 text-emerald-400/90 mt-0.5" aria-hidden />
                  <span className="leading-relaxed font-light break-words min-w-0">{line}</span>
                </li>
              ))}
            </ul>
            {copy.planPremiumDifferentialHeading && copy.planPremiumDifferential ? (
              <div className="mb-5 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-3.5 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-200/80 mb-2">{copy.planPremiumDifferentialHeading}</p>
                <p className="text-xs text-white/55 leading-relaxed font-light break-words">{copy.planPremiumDifferential}</p>
              </div>
            ) : null}
            {copy.planPremiumIdealHeading && copy.planPremiumIdeal?.length ? (
              <div className="mb-5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-white/35 mb-2">{copy.planPremiumIdealHeading}</p>
                <ul className="space-y-2">
                  {copy.planPremiumIdeal.map((line) => (
                    <li key={line} className="flex gap-2 text-xs text-white/50 leading-relaxed min-w-0">
                      <Check className="w-3.5 h-3.5 shrink-0 text-emerald-400/70 mt-0.5" aria-hidden />
                      <span className="break-words min-w-0">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {copy.planPremiumFootnote ? (
              <p className="text-[11px] text-white/38 mb-4 leading-relaxed">{copy.planPremiumFootnote}</p>
            ) : null}
            <div className="flex flex-col sm:flex-row gap-2.5 mt-auto">
              <Button
                type="button"
                className="rounded-xl flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold shadow-md shadow-emerald-950/30"
                onClick={onWhatsapp}
              >
                {copy.planCtaWhatsapp}
              </Button>
              <Button asChild variant="outline" className="rounded-xl flex-1 border-white/20 bg-white/[0.06] text-white hover:bg-white/10">
                <a href={demoUrl} target="_blank" rel="noopener noreferrer">
                  {copy.planPremiumCtaActivation || copy.planCtaDemo}
                </a>
              </Button>
            </div>
          </div>
        </article>
      </div>

      <div className="mt-10 rounded-2xl border border-white/[0.06] bg-slate-950/40 px-4 py-5 sm:px-6 text-center">
        <p className="text-xs sm:text-sm text-white/45 leading-relaxed max-w-3xl mx-auto font-light">{copy.plansInfraNote}</p>
        <p className="text-[11px] text-white/35 mt-3 max-w-2xl mx-auto leading-relaxed">{copy.plansLimitsEvolutionNote}</p>
      </div>
    </section>
  );
}
